import { Divider, ModuleSalaryIcon, SearchIcon, Stack, ThemeSpaceVariable } from "@caspeco/casper-ui-library";
import _ from "lodash";
import { useEffect, useMemo } from "react";

import { useFilterSettings } from "../../hooks/use-filter-settings";
import { useUserContext } from "../../hooks/use-user-context";
import { Salary } from "../../models/salary/salary";
import { useSalaryQuery } from "./hooks/use-salary-query";
import SalaryItem from "./salary-item";
import NewsSkeleton from "../home/news/news-skeleton";
import EmptyState from "../../components/empty-state";
import { useTranslation } from "react-i18next";
import { formatDateString } from "../../utils/date-helper";
import { useSystemHelper } from "../../hooks/use-system-helper";
import { SalaryItem as SalaryItemDTO } from "../../models/dto/salary/salary-item";

type SalaryListProps = {
	setIsRefetching: (state: boolean) => void;
	searchText?: string;
};

const SalaryList = (props: SalaryListProps) => {
	const { searchText, setIsRefetching } = props;
	const { systems } = useUserContext();
	const { t } = useTranslation();
	const [filterSettings] = useFilterSettings();
	const { getSystemName } = useSystemHelper();
	const activeSystems = systems.filter((s) => !filterSettings.disabledSystems.includes(s.name));
	const { isLoading, salaryQueries } = useSalaryQuery(activeSystems);

	const salaries = salaryQueries
		.flatMap((nq) => nq.data)
		.filter((n): n is Salary => !!n)
		.sort((a, b) => {
			if (a.reportDate < b.reportDate) {
				return 1;
			} else {
				return -1;
			}
		});

	const salaryItems: SalaryItemDTO[] = useMemo(
		() =>
			salaries.map((x) => ({
				...x,
				title: `${t("salary")} ${formatDateString(x.reportDate, "MMMM")}`,
				systemName: getSystemName(x.system),
			})),
		[salaries, t, getSystemName],
	);

	const filteredItems = useMemo(() => {
		if (searchText && searchText.length > 1) {
			return salaryItems.filter(
				(x) =>
					x.title.toLowerCase().includes(searchText.toLowerCase()) ||
					x.systemName.toLowerCase().includes(searchText.toLowerCase()),
			);
		}
		return salaryItems;
	}, [salaryItems, searchText]);

	useEffect(() => {
		if (salaryQueries.some((x) => x.isRefetching)) {
			setIsRefetching(true);
		} else {
			setIsRefetching(false);
		}
	}, [salaryQueries, setIsRefetching]);

	if (!isLoading && filteredItems.length === 0) {
		if (searchText) {
			return (
				<EmptyState
					icon={SearchIcon}
					title="search_no_items_found_title"
					text={"search_no_items_found_text"}
				/>
			);
		}
		return <EmptyState icon={ModuleSalaryIcon} title="salary_no_items_title" text={"salary_no_items_text"} />;
	}

	return (
		<>
			{isLoading ? (
				<NewsSkeleton numberOfSkeletons={3} />
			) : (
				<Stack divider={<Divider />} p={ThemeSpaceVariable.Small} gap={ThemeSpaceVariable.Medium}>
					{filteredItems.map((s, i) => (
						<SalaryItem salary={s} key={i} />
					))}
				</Stack>
			)}
		</>
	);
};

export default SalaryList;
